import { useRouterState } from "@tanstack/react-router";
import { useEffect, useMemo, useRef } from "react";
import { useOrganization } from "@/contexts/organization-context";
import { useCurrentUser } from "@/hooks/use-current-user";
import {
  captureAnalyticsEvent,
  identifyAnalyticsUser,
  registerAnalyticsProperties,
  resetAnalytics,
  setAnalyticsGroup,
} from "@/lib/analytics";
import { getPostHogAnalyticsConfig } from "@/lib/analytics/config";
import {
  createAnalyticsGlobalProperties,
  getWorkspaceAnalyticsContext,
} from "@/lib/analytics/properties";

export function PostHogContextSync() {
  const currentUser = useCurrentUser();
  const { organizationId } = useOrganization();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const previousProjectIdRef = useRef<string | undefined>(undefined);
  const previousPathnameRef = useRef<string | undefined>(undefined);

  const analyticsConfig = useMemo(() => getPostHogAnalyticsConfig(), []);
  const routeContext = useMemo(() => getWorkspaceAnalyticsContext(pathname), [pathname]);

  useEffect(() => {
    if (!currentUser?._id) {
      resetAnalytics();
      return;
    }

    identifyAnalyticsUser({
      userId: currentUser._id,
      orgId: organizationId ?? undefined,
    });
  }, [currentUser?._id, organizationId]);

  useEffect(() => {
    setAnalyticsGroup("organization", organizationId ?? undefined);
  }, [organizationId]);

  useEffect(() => {
    setAnalyticsGroup("project", routeContext.project_id);
  }, [routeContext.project_id]);

  useEffect(() => {
    registerAnalyticsProperties(
      createAnalyticsGlobalProperties({
        environment: analyticsConfig.environment,
        release: analyticsConfig.release,
        orgId: organizationId ?? undefined,
        pathname,
      })
    );
  }, [analyticsConfig.environment, analyticsConfig.release, organizationId, pathname]);

  useEffect(() => {
    if (previousPathnameRef.current === pathname) return;
    previousPathnameRef.current = pathname;

    captureAnalyticsEvent("page_viewed", routeContext);
  }, [pathname, routeContext]);

  useEffect(() => {
    const projectId = routeContext.project_id;
    if (!projectId || previousProjectIdRef.current === projectId) return;
    previousProjectIdRef.current = projectId;

    captureAnalyticsEvent("project_opened", {
      project_id: projectId,
      entry_surface: "route",
    });
  }, [routeContext.project_id]);

  return null;
}
